import { useState, useEffect } from 'react';
import { prospectsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const prospectLabel = (p) =>
    p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Dossier sans nom';

export function ProspectsTrashPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, prospect: null });

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            setLoading(true);
            const res = await prospectsAPI.getTrash();
            setItems(res.data || []);
        } catch (error) {
            console.error('Error loading trash:', error);
            toast.error('Erreur lors du chargement de la corbeille');
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (prospect) => {
        try {
            setProcessing(true);
            await prospectsAPI.restore(prospect.id);
            toast.success(`Dossier « ${prospectLabel(prospect)} » restauré`);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
        } finally {
            setProcessing(false);
        }
    };

    const handlePermanentDelete = async () => {
        if (!confirmDialog.prospect) return;
        try {
            setProcessing(true);
            await prospectsAPI.permanentDelete(confirmDialog.prospect.id);
            toast.success('Dossier supprimé définitivement');
            setConfirmDialog({ open: false, prospect: null });
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression définitive');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Card data-testid="prospects-trash-panel">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Trash2 className="w-5 h-5 text-amber-600" />
                        Corbeille des dossiers
                        {items.length > 0 && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800">{items.length}</Badge>
                        )}
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="trash-refresh-btn">
                        Rafraîchir
                    </Button>
                </div>
                <p className="text-sm text-slate-500">
                    Les dossiers supprimés sont conservés ici. Vous pouvez les restaurer ou les supprimer définitivement.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-center py-8 text-slate-500">Chargement...</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">La corbeille est vide</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Dossier</TableHead>
                                <TableHead>Supprimé par</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((p) => (
                                <TableRow key={p.id} data-testid={`trash-row-${p.id}`}>
                                    <TableCell className="font-medium">{prospectLabel(p)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{p.deleted_by_name || '—'}</TableCell>
                                    <TableCell className="text-sm text-slate-500">
                                        {p.deleted_at ? format(new Date(p.deleted_at), 'dd/MM/yyyy HH:mm', { locale: fr }) : '—'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-green-600 border-green-300 hover:bg-green-50"
                                                onClick={() => handleRestore(p)}
                                                disabled={processing}
                                                data-testid={`restore-btn-${p.id}`}
                                            >
                                                <RotateCcw className="w-4 h-4 mr-1" /> Restaurer
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 border-red-300 hover:bg-red-50"
                                                onClick={() => setConfirmDialog({ open: true, prospect: p })}
                                                disabled={processing}
                                                data-testid={`permanent-delete-btn-${p.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, prospect: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Suppression définitive
                        </DialogTitle>
                        <DialogDescription className="pt-4">
                            <span className="block mb-2">
                                Supprimer <strong>définitivement</strong> le dossier :
                            </span>
                            <span className="block font-semibold text-lg text-slate-900 mb-4">
                                {confirmDialog.prospect && prospectLabel(confirmDialog.prospect)}
                            </span>
                            <span className="block bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                                Cette action est irréversible : le dossier et toutes ses données liées
                                (documents, commissions, tâches) seront détruits.
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmDialog({ open: false, prospect: null })} disabled={processing}>
                            Annuler
                        </Button>
                        <Button variant="destructive" onClick={handlePermanentDelete} disabled={processing} data-testid="confirm-permanent-delete-btn">
                            {processing ? 'Suppression...' : 'Supprimer définitivement'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default ProspectsTrashPanel;
